import type { StatusTone } from '@repletoryreach/sdk-ui/data-display';

/**
 * The quality ratings Meta reports on a number.
 *
 * LOW used to be missing from the tone map, the KPI chips and the filter
 * options while the page's own description told operators to act on it, so a
 * LOW-rated number rendered grey, matched no chip and could not be filtered
 * to. One list now drives the colours, the chips, the filter and the counts, so
 * the three cannot drift apart again.
 */
export const QUALITY_RATINGS = ['GREEN', 'YELLOW', 'LOW', 'RED'] as const;
export type QualityRating = (typeof QUALITY_RATINGS)[number];

const TONES: Record<QualityRating, StatusTone> = {
  GREEN: 'healthy',
  YELLOW: 'degraded',
  LOW: 'warning',
  RED: 'down'
};

/** The rating in its canonical spelling, or null when Meta has not rated the number. */
export function normaliseRating(value: string | null | undefined): QualityRating | 'OTHER' | null {
  const raw = (value ?? '').trim().toUpperCase();
  if (!raw) return null;
  return (QUALITY_RATINGS as readonly string[]).includes(raw) ? (raw as QualityRating) : 'OTHER';
}

export function qualityTone(value: string | null | undefined): StatusTone {
  const rating = normaliseRating(value);
  return rating && rating !== 'OTHER' ? TONES[rating] : 'inactive';
}

export interface QualityCounts {
  GREEN: number;
  YELLOW: number;
  LOW: number;
  RED: number;
  /** Meta has sent no rating for the number yet. */
  unrated: number;
  /** A rating this build does not know, kept visible rather than silently lost. */
  other: number;
}

export function countByRating(rows: Array<{ quality_rating: string | null | undefined }>): QualityCounts {
  const counts: QualityCounts = { GREEN: 0, YELLOW: 0, LOW: 0, RED: 0, unrated: 0, other: 0 };
  for (const row of rows) {
    const rating = normaliseRating(row.quality_rating);
    if (rating === null) counts.unrated += 1;
    else if (rating === 'OTHER') counts.other += 1;
    else counts[rating] += 1;
  }
  return counts;
}
